/**
 * Individual facet filter component.
 *
 * Displays as an expandable accordion with checkboxes.
 * Stores IDs in URL params for cross-language persistence.
 * Preserves original ordering of options when selections are made.
 */

'use client';

import { Icon } from '@/components/ui/Icon';
import { extractFacetId, extractFacetLabel, facetValueMatchesId } from '@/lib/algolia/facetValues';
import { useSearchParams } from '@/lib/algolia/hooks/useSearchParams';
import { devLog } from '@/lib/utils/devLog';
import { useMemo, useRef, useState } from 'react';
import styles from './FacetFilter.module.scss';

interface FacetFilterProps {
  name: string;
  displayName: string;
  values: [string, number][]; // [fullValue, count]
  activeIds: string[]; // IDs from URL params
}

/**
 * All supported filter parameter names for facets.
 * These map to URL parameters via useSearchParams.
 */
type FacetParamName =
  // Watch filters
  | 'collection'
  | 'size'
  | 'case'
  | 'dial-color'
  | 'type'
  | 'bracelet-material'
  // Strap filters
  | 'material'
  | 'color'
  | 'lug'
  | 'lug-type'
  | 'buckle'
  // Eyewear filters
  | 'frame-material'
  | 'lens-color'
  // Store filters
  | 'store-type';

/**
 * Map base facet names to URL parameter names.
 * This allows FacetFilter to handle all product types.
 */
const facetToParam: Record<string, FacetParamName> = {
  // Watch facets
  collection: 'collection',
  size: 'size',
  case: 'case',
  dial_color: 'dial-color',
  type: 'type',
  bracelet_category_material: 'bracelet-material',
  // Strap facets
  material: 'material',
  color: 'color',
  lug_size: 'lug',
  lug_type: 'lug-type',
  buckle_type: 'buckle',
  // Eyewear facets
  frame_material: 'frame-material',
  lens_color: 'lens-color',
  // Store facets
  store_type: 'store-type',
};

export function FacetFilter({ name, displayName, values, activeIds }: FacetFilterProps) {
  const [isExpanded, setIsExpanded] = useState(activeIds.length > 0);
  const { toggleFilterValue } = useSearchParams();

  // Track original order of facet values to prevent reordering on selection
  const originalOrderRef = useRef<string[] | null>(null);

  const paramName = facetToParam[name];

  // The `size` facet stores raw numbers (e.g. "38.0"); show them as "38 mm".
  const formatOptionLabel = (rawLabel: string): string => {
    if (name !== 'size') return rawLabel;
    const numeric = Number.parseFloat(rawLabel);
    return Number.isNaN(numeric) ? rawLabel : `${numeric} mm`;
  };

  // Debug logging
  devLog(`[FacetFilter] name=${name}, paramName=${paramName}, activeIds=`, activeIds);
  devLog(`[FacetFilter] values sample (first 3):`, values.slice(0, 3));

  // Parse values to extract IDs and labels
  // Note: useMemo must be called unconditionally (before any early returns)
  const parsedValues = useMemo(() => {
    const parsed = values.map(([fullValue, count]) => {
      const id = extractFacetId(fullValue, name);
      const label = extractFacetLabel(fullValue, name);
      const isActive = activeIds.some((activeId) => {
        const matches = facetValueMatchesId(fullValue, activeId, name);
        if (activeIds.length > 0) {
          devLog(
            `[FacetFilter] Checking match: fullValue="${fullValue}", activeId="${activeId}", extractedId="${id}", matches=${matches}`
          );
        }
        return matches;
      });
      return {
        fullValue,
        id,
        label,
        count,
        isActive,
      };
    });

    // Capture original order on first render (when no active filters)
    if (originalOrderRef.current === null && parsed.length > 0) {
      originalOrderRef.current = parsed.map((p) => p.id);
    }

    // Sort by original order to prevent reordering when selections change
    if (originalOrderRef.current) {
      const orderMap = new Map(originalOrderRef.current.map((id, index) => [id, index]));
      parsed.sort((a, b) => {
        const orderA = orderMap.get(a.id) ?? Infinity;
        const orderB = orderMap.get(b.id) ?? Infinity;
        return orderA - orderB;
      });
    }

    // Log active items
    const activeItems = parsed.filter((p) => p.isActive);
    if (activeIds.length > 0) {
      devLog(`[FacetFilter] Active items found:`, activeItems);
    }

    return parsed;
  }, [values, name, activeIds]);

  // Early return if no valid param name (after hooks are called)
  if (!paramName) return null;

  const handleToggle = (id: string) => {
    // Store the ID in URL params (not the full localized value)
    toggleFilterValue(paramName, id);
  };

  return (
    <div className={`${styles.facet} ${isExpanded ? styles.expanded : ''}`}>
      <button
        type="button"
        className={styles.header}
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
      >
        <span className={styles.name}>
          {displayName}
          {activeIds.length > 0 && <span className={styles.activeCount}>{activeIds.length}</span>}
        </span>
        <Icon
          name="chevron-down"
          size={20}
          className={`${styles.chevron} ${isExpanded ? styles.expanded : ''}`}
        />
      </button>

      {isExpanded && (
        <div className={styles.values}>
          {parsedValues.map(({ fullValue, id, label, count, isActive }) => {
            const inputId = `facet-${name}-${id}`;

            return (
              <label key={fullValue} htmlFor={inputId} className={styles.option}>
                <input
                  type="checkbox"
                  id={inputId}
                  checked={isActive}
                  onChange={() => handleToggle(id)}
                  className={styles.checkbox}
                />
                <span className={styles.labelWrapper}>
                  <span className={styles.label}>{formatOptionLabel(label)}</span>
                  <span className={styles.count}>({count})</span>
                </span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}
